import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Loader2, ChevronLeft, Lock, CheckCircle2, Circle, Video, Calendar,
  FileText, ImageIcon, File, Download, ChevronDown, ChevronRight, Paperclip,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  fetchProductAccess, fetchProductReviews, submitProductReview, fetchProductProgress,
  markContentComplete, fetchMuxPlaybackToken, fetchJoinLink, isPortraitProduct, formatClock,
  type CreatorProduct, type SubscriptionInfo, type ProductReview, type MuxPlaybackTokens,
  type PlusOfferState, type BundleSummary,
} from '@/services/creatorApi';
import { trackPurchase } from '@/lib/marketing';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { formatEventDay, formatEventLong } from '@/lib/eventTime';
import { NextEventCrossSell } from '@/components/NextEventCrossSell';
import { ReferralShareCard } from '@/components/ReferralShareCard';
import { ReplayQuestions } from '@/components/ReplayQuestions';

// Mux Player is ~300KB gzipped — lazy load so it only ships when a buyer
// actually opens a paid product.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const ProductViewerPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [product, setProduct] = useState<CreatorProduct | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  // "Includes N months of Hey Joe Plus" bundle state, driven by the /access
  // response. Coupon is auto-applied — no code is ever typed.
  const [plusOffer, setPlusOffer] = useState<PlusOfferState | null>(null);
  // A bundle or season pass: its page is the list of what it unlocked.
  const [bundle, setBundle] = useState<BundleSummary | null>(null);
  // Plus-membership upsell after purchase — the '2 months free' code from the
  // featured event gets entered on the Stripe checkout page (allow_promotion_codes).
  const plus = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replayExpired, setReplayExpired] = useState(false);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mux playback tokens keyed by videoIdx. Fetched on lesson select. Null = "tried
  // and got 503 (Mux unconfigured) or 409 (not ready)" → falls back to S3 below.
  const [muxTokens, setMuxTokens] = useState<Record<number, MuxPlaybackTokens | null>>({});

  // Replay chapters: markers on the Mux scrub bar plus a clickable list under the
  // player. The ref points at whichever element is mounted (Mux player or the S3
  // <video> fallback); both expose currentTime and the media events we listen to.
  // Callback ref, not useRef: the Mux player is lazy-loaded, so it mounts after the
  // token arrives and a plain ref would be null when the effect ran. State makes
  // the listener effect re-run the moment the element exists.
  const [playerEl, setPlayerEl] = useState<any>(null);
  const playerRef = useCallback((el: any) => setPlayerEl(el), []);
  const [playheadSec, setPlayheadSec] = useState(0);
  const replayChapters = product?.replayChapters && product.replayChapters.chapters?.length
    && product.replayChapters.videoIndex === activeVideoIndex ? product.replayChapters : null;
  useEffect(() => {
    const el = playerEl;
    if (!el || !replayChapters) return;
    const addChapters = () => {
      try {
        if (typeof el.addChapters === 'function') {
          el.addChapters(replayChapters.chapters.map(c => ({ startTime: c.startSec, endTime: c.endSec, value: c.title })));
        }
      } catch { /* markers are a nicety; the list still seeks */ }
    };
    const onTime = () => { const t = Math.floor(el.currentTime || 0); setPlayheadSec(prev => (prev === t ? prev : t)); };
    if (el.readyState >= 1) addChapters(); else el.addEventListener('loadedmetadata', addChapters, { once: true });
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('seeked', onTime);
    return () => { el.removeEventListener('loadedmetadata', addChapters); el.removeEventListener('timeupdate', onTime); el.removeEventListener('seeked', onTime); };
  }, [playerEl, replayChapters]);
  const activeChapterIndex = replayChapters
    ? replayChapters.chapters.findIndex(c => playheadSec >= c.startSec && playheadSec < c.endSec)
    : -1;
  // Keep the playing chapter in view inside the list's own scroll box, never by
  // scrolling the page (that would pull the reader away from the video).
  const chapterListRef = useRef<HTMLOListElement | null>(null);
  useEffect(() => {
    const list = chapterListRef.current;
    if (!list || activeChapterIndex < 0) return;
    const item = list.children[activeChapterIndex] as HTMLElement | undefined;
    if (!item) return;
    const top = item.offsetTop - list.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top < list.scrollTop || bottom > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: Math.max(0, top - 12), behavior: 'smooth' });
    }
  }, [activeChapterIndex]);
  const seekTo = (sec: number) => {
    const el = playerEl;
    if (!el) return;
    try { el.currentTime = sec; const p = el.play?.(); if (p && typeof p.catch === 'function') p.catch(() => {}); } catch { /* ignore */ }
    setPlayheadSec(Math.floor(sec));
  };

  // Descriptions arrive as plain text with blank-line paragraphs and "•"/"-" bullet
  // lines. Render that structure instead of one collapsed wall of text.
  const renderDescription = (text: string) => {
    const blocks: React.ReactNode[] = [];
    let list: string[] = [];
    const flushList = () => {
      if (!list.length) return;
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="m-0" style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {list.map((li, i) => <li key={i} style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--hj-beige-ink)' }}>{li}</li>)}
        </ul>
      );
      list = [];
    };
    for (const rawLine of text.split('\n')) {
      const line = rawLine.trim();
      if (!line) { flushList(); continue; }
      const bullet = line.match(/^[•\-*·]\s+(.*)$/);
      if (bullet) { list.push(bullet[1]); continue; }
      flushList();
      const isHeading = line.length <= 60 && !/[.!?:]$/.test(line) && /^[A-Z]/.test(line) && line.split(' ').length <= 8;
      blocks.push(
        isHeading
          ? <h3 key={`h-${blocks.length}`} className="m-0" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-beige-ink)', marginTop: 6 }}>{line}</h3>
          : <p key={`p-${blocks.length}`} className="m-0" style={{ fontSize: 14.5, lineHeight: 1.6, color: 'var(--hj-beige-ink)' }}>{line}</p>
      );
    }
    flushList();
    return blocks;
  };
  const [aboutOpen, setAboutOpen] = useState(false);
  const hasReplayVideo = product?.productType === 'webinar' && (product.videos || []).some((v: any) => v.muxPlaybackId || v.s3Key);
  const [muxRetried, setMuxRetried] = useState<Record<number, boolean>>({});
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewHover, setReviewHover] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<string, boolean>>({});
  const [completionPercent, setCompletionPercent] = useState(0);
  const [togglingProgress, setTogglingProgress] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [joinInfo, setJoinInfo] = useState<{ joinUrl: string; joinProvider: string; eventDate?: string; durationMins?: number | null } | null>(null);

  // Featured event: once entitled, fetch the private join link.
  useEffect(() => {
    if (product?.productType === 'webinar' && productId) {
      fetchJoinLink(productId).then(setJoinInfo).catch(() => setJoinInfo(null));
    }
  }, [product?.productType, productId]);

  useEffect(() => {
    if (searchParams.get('purchased') === 'success') {
      toast({ title: 'Purchase complete!', description: 'Enjoy your content.' });
      // Tell Meta a sale happened. Nothing did this before, so the pixel saw
      // checkouts start and never finish.
      const sessionId = searchParams.get('session_id');
      if (sessionId && productId) {
        trackPurchase({ eventId: sessionId, productId, title: product?.title, valueCents: product?.price });
      }
    }
  }, [searchParams, productId, product?.title, product?.price]);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);

    const loadAccess = () => {
      fetchProductAccess(productId)
        .then(data => {
          setProduct(data.product);
          setSubscription(data.subscription || null);
          setPlusOffer(data.plusOffer || null);
          setBundle(data.bundle || null);
          const expiresIn = data.expiresIn || 900;
          const refreshMs = Math.max((expiresIn - 120) * 1000, 60000);
          refreshTimerRef.current = setTimeout(loadAccess, refreshMs);
        })
        .catch(err => { if (err.replayExpired) setReplayExpired(true); setError(err.message); })
        .finally(() => setLoading(false));
    };

    loadAccess();
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    fetchProductReviews(productId)
      .then(data => {
        setReviews(data.reviews);
        setAverageRating(data.averageRating);
        setTotalReviews(data.totalReviews);
      })
      .catch(() => {});
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    fetchProductProgress(productId)
      .then(data => {
        const map: Record<string, boolean> = {};
        data.progress.forEach(p => { map[p.s3Key] = p.completed; });
        setProgressMap(map);
        setCompletionPercent(data.completionPercent);
      })
      .catch(() => {});
  }, [productId]);

  // Mux per-lesson token fetch — fires when activeVideoIndex changes and the
  // selected video has a ready Mux asset. Fallback to S3 happens in the render
  // path if this stays unset / errors out.
  useEffect(() => {
    if (!productId || !product) return;
    const v = product.videos[activeVideoIndex];
    if (!v) return;
    if ((v.fileType || 'video') !== 'video') return;
    if (v.muxIngestStatus !== 'ready' || !v.muxPlaybackId) return;
    // Already have a non-expired token for this lesson? Skip.
    const existing = muxTokens[activeVideoIndex];
    if (existing && existing.tokens.playback && new Date(existing.expiresAt).getTime() > Date.now() + 60_000) return;
    let cancelled = false;
    fetchMuxPlaybackToken(productId, activeVideoIndex)
      .then(tok => { if (!cancelled) setMuxTokens(m => ({ ...m, [activeVideoIndex]: tok })); })
      .catch(err => {
        console.warn('[mux] token fetch failed for video', activeVideoIndex, err);
        if (!cancelled) setMuxTokens(m => ({ ...m, [activeVideoIndex]: null }));
      });
    return () => { cancelled = true; };
  }, [productId, product, activeVideoIndex]);

  const refetchMuxToken = async (idx: number) => {
    if (!productId) return;
    if (muxRetried[idx]) return; // one retry per lesson
    setMuxRetried(r => ({ ...r, [idx]: true }));
    try {
      const tok = await fetchMuxPlaybackToken(productId, idx);
      setMuxTokens(m => ({ ...m, [idx]: tok }));
    } catch (err) {
      console.warn('[mux] token refetch failed', err);
    }
  };

  const handleToggleComplete = async (s3Key: string) => {
    if (!productId || togglingProgress) return;
    const currentlyComplete = !!progressMap[s3Key];
    setTogglingProgress(true);
    try {
      await markContentComplete(productId, s3Key, !currentlyComplete);
      const newMap = { ...progressMap, [s3Key]: !currentlyComplete };
      setProgressMap(newMap);
      if (product) {
        const totalItems = product.videos.filter(v => !v.locked).length;
        const completedItems = Object.values(newMap).filter(Boolean).length;
        setCompletionPercent(totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0);
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setTogglingProgress(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!productId || reviewRating < 1) return;
    setSubmittingReview(true);
    try {
      await submitProductReview(productId, reviewRating, reviewComment);
      toast({ title: 'Review submitted!' });
      const data = await fetchProductReviews(productId);
      setReviews(data.reviews);
      setAverageRating(data.averageRating);
      setTotalReviews(data.totalReviews);
      setReviewRating(0);
      setReviewComment('');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <main
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: 'var(--hj-bg)' }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-2)' }} />
        <p className="m-0" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-ink-2)' }}>
          Loading content…
        </p>
      </main>
    );
  }

  if (replayExpired) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
        <div className="text-center" style={{ maxWidth: 460 }}>
          <Lock className="w-10 h-10 mx-auto" style={{ color: 'var(--hj-ink-2)', marginBottom: 16 }} />
          <h1 className="font-hj-display m-0" style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}>
            Your replay window has ended.
          </h1>
          <p style={{ marginTop: 12, fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.6 }}>
            One-time replays stay open for three months. <strong>Hey Joe Plus members keep every replay they unlock — for as long as they’re members.</strong> Join and this one comes right back, with your whole replay library.
          </p>
          <Link
            to="/plans?utm_source=replay-expired&utm_medium=product&utm_campaign=replay-library"
            className="inline-flex items-center justify-center gap-1.5"
            // Yellow pill, dark text — the app's standard primary button. This was
            // background --hj-ink with --hj-yellow text, written as "black pill,
            // yellow label"; but --hj-ink is near-WHITE in the dark theme, so it
            // rendered yellow on white and the only call to action on the page was
            // unreadable.
            style={{ marginTop: 22, padding: '12px 26px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 700, textDecoration: 'none', minHeight: 44 }}
          >
            Keep it with Hey Joe Plus →
          </Link>
          <div style={{ marginTop: 14 }}>
            <Link to="/my-purchases" style={{ fontSize: 13, color: 'var(--hj-ink-2)', textDecoration: 'none' }}>Back to my purchases</Link>
          </div>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
      >
        <div className="text-center" style={{ maxWidth: 420 }}>
          <Lock className="w-10 h-10 mx-auto" style={{ color: 'var(--hj-ink-2)', marginBottom: 16 }} />
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}
          >
            {error === 'Access denied' ? 'Not purchased.' : 'Product not found.'}
          </h1>
          <p style={{ marginTop: 12, fontSize: 14, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>
            {error === 'Access denied'
              ? 'If you registered or purchased this, make sure you’re signed in with the same email you used — then it’ll unlock here.'
              : 'This product may no longer be available.'}
          </p>
          <Link
            to="/my-purchases"
            className="inline-flex items-center gap-1.5"
            style={{
              marginTop: 20,
              padding: '8px 16px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 13,
              fontWeight: 500,
              textDecoration: 'none',
              fontFamily: 'inherit',
              minHeight: 40,
            }}
          >
            <ChevronLeft className="w-3.5 h-3.5" /> My purchases
          </Link>
        </div>
      </main>
    );
  }

  const activeVideo = product.videos[activeVideoIndex];
  const isActiveLocked = activeVideo?.locked;

  // A featured event is "past" once its last occurrence (+ runtime) has elapsed — the live
  // join link is dead and the replay plays below, so the Join button gets disabled.
  const webinarPast = (() => {
    if (product.productType !== 'webinar') return false;
    const occ = ((product as any).eventDates?.length ? (product as any).eventDates : (product.eventDate ? [product.eventDate] : []))
      .map((d: string) => new Date(d).getTime()).filter((t: number) => isFinite(t));
    if (!occ.length) return false;
    return !occ.some((t: number) => t + ((product.durationMins || 60) * 60000) > Date.now());
  })();

  // Attachments live on the module lesson (chapter files), not the flat video.
  // Match by s3Key to surface the current lesson's downloads under the player.
  const activeAttachments = (() => {
    if (!activeVideo?.s3Key || !product.modules) return [];
    for (const m of product.modules) {
      const lesson = (m.lessons || []).find(l => l.s3Key === activeVideo.s3Key);
      if (lesson?.attachments?.length) return lesson.attachments;
    }
    return [];
  })();

  // Vertical-first when this episode is portrait, or (orientation unknown) the
  // product is majority-portrait. Drives 9:16 letterboxing of the player.
  const productIsPortrait = isPortraitProduct(product.videos);
  const isPortrait = activeVideo?.orientation === 'portrait' || (!activeVideo?.orientation && productIsPortrait);

  // Binge behavior: when an episode ends, jump to the next unlocked one.
  const goToNextUnlocked = (from: number) => {
    for (let i = from + 1; i < product.videos.length; i++) {
      if (!product.videos[i]?.locked) { setActiveVideoIndex(i); return; }
    }
  };

  if (product.productType === 'bundle') {
    const isPass = bundle?.mode === 'pass';
    const items = bundle?.items || [];
    return (
      <main className="mx-auto" style={{ maxWidth: 760, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/my-purchases" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-2)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} />
            My Purchases
          </Link>
        </div>
        {(searchParams.get('enrolled') === 'free' || searchParams.get('purchased') === 'success') && (
          <ReferralShareCard productId={productId} eventTitle={product.title} style={{ margin: '4px 0 16px' }} />
        )}
        <header style={{ margin: '4px 0 20px', padding: '22px 20px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
          <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-beige-ink-2)' }}>{isPass ? 'Season pass' : 'Bundle'}</div>
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(26px, 4vw, 38px)', lineHeight: 1.05, color: 'var(--hj-beige-ink)', letterSpacing: '-0.025em', fontWeight: 700 }}>
            {product.title}
          </h1>
          <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.5, color: 'var(--hj-beige-ink)' }}>
            {isPass
              ? `You're registered for every event below${bundle?.through ? ` and anything added through ${new Date(bundle.through).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' })}` : ''}. Each one emails you its link.`
              : "Everything in the bundle is yours. Open each one below; events email you their link."}
          </p>
        </header>
        <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
          {items.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: 14 }}>{isPass ? 'Nothing is scheduled yet. New events show up here as they are announced.' : 'Nothing to show yet.'}</div>
          )}
          {items.map((it, i) => {
            const img = it.coverImage ? (it.coverImage.startsWith('http') || it.coverImage.startsWith('/api/') ? it.coverImage : `/api/s3/${it.coverImage}`) : null;
            const when = (it.sessions[0] || it.eventDate) ? formatEventDay(it.sessions[0] || it.eventDate!) : null;
            return (
              <Link key={it._id} to={`/purchases/${it._id}`} className="flex items-center gap-3"
                style={{ padding: '12px 14px', borderTop: i ? '1px solid var(--hj-hairline-soft)' : 'none', color: 'var(--hj-ink)', textDecoration: 'none', minHeight: 56 }}>
                {img ? <img src={img} alt="" style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                  : <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 64, height: 40, borderRadius: 6, background: 'var(--hj-surface-2)' }}><Video className="w-4 h-4" /></span>}
                <span className="flex-1 min-w-0">
                  <span className="block" style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.3 }}>{it.title}</span>
                  <span className="block" style={{ fontSize: 12.5, marginTop: 2 }}>
                    {when || (it.productType === 'series' ? 'Course' : 'Video')}{it.sessions.length > 1 ? ` · ${it.sessions.length} sessions` : ''}{it.tierLabel ? ` · ${it.tierLabel}` : ''}
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      </main>
    );
  }

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: replayChapters ? 1180 : 1024,   // room for the chapter panel beside a decent-sized player
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-2)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <Link to="/my-purchases" style={{ color: 'var(--hj-ink-2)', textDecoration: 'none' }}>
          My Purchases
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span className="truncate" style={{ color: 'var(--hj-ink)', maxWidth: 240 }}>
          {product.title}
        </span>
      </div>

      {/* Just registered or just bought: show what's on next, if it's soon. Free
          enrolment lands here with ?enrolled=free, paid checkout with
          ?purchased=success — both are the moment we know they're looking. */}
      {(searchParams.get('enrolled') === 'free' || searchParams.get('purchased') === 'success') && (
        <>
          {/* The sharing moment: they just registered and are looking at the page.
              Members get their own link to this event; non-members see nothing. */}
          <ReferralShareCard productId={productId} eventTitle={product.title} style={{ margin: '4px 0 16px' }} />
          <NextEventCrossSell excludeProductId={productId} />
        </>
      )}

      {/* Cream header card on the dark page, same as the marketplace and the
          builders. Full-contrast ink on cream; the kicker is no longer faded. */}
      <header style={{ margin: '4px 0 20px', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
        <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-beige-ink-2)' }}>
          {product.productType === 'webinar' ? (webinarPast && hasReplayVideo ? 'Featured event replay' : 'Featured event') : product.productType === 'goods' ? 'Your download' : 'Course'}
        </div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)',
            lineHeight: 1.02,
            color: 'var(--hj-beige-ink)',
            letterSpacing: '-0.025em',
            fontWeight: 700,
          }}
        >
          {product.title}
        </h1>
        {/* Past event with a replay: one line, then the video. The full description
            moved below the player ("About this event"); it was a 1,200px wall of
            text between the title and the play button. */}
        {product.productType === 'webinar' && webinarPast && hasReplayVideo && product.eventDate && (
          <p className="m-0" style={{ marginTop: 10, color: 'var(--hj-beige-ink)', fontSize: 14.5, lineHeight: 1.5 }}>
            Recorded {formatEventDay(product.eventDate)}
            {product.durationMins ? ` · ${product.durationMins} min` : ''}
          </p>
        )}
      </header>

      {/* Files that come with it (slides, a worksheet): signed links on the entitled
          payload, shown for the live event and on the replay alike. */}
      {(product.downloads || []).some((d) => d.url) && (
        <div style={{ padding: '16px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', marginBottom: 20 }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 8 }}>Yours to download</p>
          <ul className="m-0 p-0" style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(product.downloads || []).filter((d) => d.url).map((d) => (
              <li key={d.id}>
                <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-ink)', fontWeight: 600, fontSize: 14, textDecoration: 'underline' }}>
                  {d.title}{d.bytes ? <span style={{ fontWeight: 400, color: 'var(--hj-ink-2)' }}> · {(d.bytes / 1048576).toFixed(1)} MB</span> : null}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Featured event: registration confirmed + private join link */}
      {product.productType === 'webinar' && !(webinarPast && hasReplayVideo) && (
        <div style={{ padding: '18px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-yellow)', marginBottom: 20 }}>
          <div className="mono-label" style={{ marginBottom: 6, color: 'var(--hj-ink)' }}>{webinarPast ? '✓ Replay available · watch below' : "✓ You're registered · Live · Online"}</div>
          {product.eventDate && (
            <p className="m-0" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)' }}>
              {formatEventLong(product.eventDate)}
              {product.durationMins ? ` · ${product.durationMins} min` : ''}
            </p>
          )}
          {/* A dead "Featured event has ended" chip directly above a playable replay reads as
              "you missed it" when the recording is right there. Only show it when the
              event is over AND there is nothing to watch. */}
          {webinarPast && !(product.videos || []).some((v: any) => v.muxPlaybackId || v.s3Key) ? (
            <span aria-disabled="true" className="inline-flex items-center justify-center gap-2"
              style={{ marginTop: 12, padding: '12px 22px', borderRadius: 999, background: 'var(--hj-surface-2)', color: 'var(--hj-ink-4)', fontSize: 14, fontWeight: 700, cursor: 'not-allowed', border: '1px solid var(--hj-hairline)' }}>
              Featured event has ended
            </span>
          ) : webinarPast ? null : joinInfo?.joinUrl ? (
            <a href={joinInfo.joinUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2"
              style={{ marginTop: 12, padding: '12px 22px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
              {(product as any)?.joinLabel || 'Join the featured event'} →
            </a>
          ) : (
            <p className="m-0" style={{ marginTop: 10, fontSize: 13, color: 'var(--hj-ink-3)' }}>
              Your join link will appear here — it goes live at start time. We'll also email everyone the recording afterward.
            </p>
          )}
        </div>
      )}

      {/* Membership bundle — "includes 2 months of Hey Joe Plus". The coupon is
          applied automatically (at Plus checkout for new subscribers, or straight
          to a live membership for existing ones) — nobody types a code. Prefer the
          /access plusOffer state; fall back to the raw product field if absent. */}
      {(() => {
        const offerMonths = plusOffer?.months ?? ((product as any)?.plusOfferMonths || 0);
        if (!(offerMonths > 0)) return null;

        // Claimed (existing subscriber) — coupon landed on their live membership.
        if (plusOffer?.claimed && plusOffer.claimKind === 'existing_sub_extend') {
          return (
            <div style={{ padding: '16px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-yellow)', marginBottom: 20 }}>
              <p className="m-0" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>✓ Your {offerMonths} free months of Hey Joe Plus are applied</p>
              <p className="m-0" style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                Your {offerMonths} free months have been applied to your membership — the next {offerMonths === 2 ? 'two renewals are' : `${offerMonths} renewals are`} on us.
              </p>
            </div>
          );
        }

        // Claimed (new subscriber) — the coupon rode their new Plus checkout.
        if (plusOffer?.claimed && plusOffer.claimKind === 'new_sub') {
          return (
            <div style={{ padding: '16px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-yellow)', marginBottom: 20 }}>
              <p className="m-0" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>✓ Your {offerMonths} free months of Hey Joe Plus are active</p>
              <p className="m-0" style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                Your {offerMonths} free months are active — welcome to Hey Joe Plus.
              </p>
            </div>
          );
        }

        // Unclaimed + not yet a Plus subscriber — the coupon applies automatically
        // at checkout. Hide once they're already on Plus (claim is pending webhook).
        if (plus.status !== 'active') {
          return (
            <div style={{ padding: '16px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-yellow)', marginBottom: 20 }}>
              <p className="m-0" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>🎁 Your {offerMonths} free months of Hey Joe Plus</p>
              <p className="m-0" style={{ marginTop: 4, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                This purchase includes {offerMonths} months of Hey Joe Plus — the discount applies automatically at checkout. Billing starts month {offerMonths + 1}.
              </p>
              <Link to={`/plans?returnTo=${encodeURIComponent(`/purchases/${productId}`)}`}
                className="inline-flex items-center justify-center"
                style={{ marginTop: 10, padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                Join Hey Joe Plus →
              </Link>
            </div>
          );
        }
        return null;
      })()}

      {/* Subscription info banner */}
      {subscription && (
        <div
          className="flex items-center gap-2"
          style={{
            padding: '10px 14px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            fontSize: 12.5,
            color: 'var(--hj-ink-2)',
            marginBottom: 20,
          }}
        >
          <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)' }}>
            Subscribed {new Date(subscription.startedAt).toLocaleDateString()}
            {subscription.status === 'active' && subscription.currentPeriodEnd && (
              <> · Renews {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</>
            )}
            {subscription.status === 'canceled' && subscription.currentPeriodEnd && (
              <> · Access ends {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</>
            )}
          </span>
        </div>
      )}

      {/* Progress Bar */}
      {product.videos.length > 1 && (
        <div style={{ marginBottom: 22 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
            <span
              className="tabular"
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}
            >
              {completionPercent}% complete
            </span>
            <span className="mono-label" style={{ color: 'var(--hj-ink-2)' }}>
              {Object.values(progressMap).filter(Boolean).length} / {product.videos.filter(v => !v.locked).length}
            </span>
          </div>
          <div
            className="w-full overflow-hidden"
            style={{ height: 8, borderRadius: 999, background: 'var(--hj-surface-2)' }}
          >
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${completionPercent}%`,
                background: 'var(--hj-blue)',
                borderRadius: 999,
              }}
            />
          </div>
        </div>
      )}

      {/* Player + chapters side by side on wide screens; the chapter list scrolls on
          its own so the video never leaves the viewport. Stacked on phones. */}
      <div
        className={replayChapters ? 'hj-replay-grid' : undefined}
      >
      {replayChapters && (
        <style>{`
          /* Desktop: the video sets the row height and the chapter panel fills it
             exactly (absolute inside its cell), so the two bottoms line up and the
             list scrolls inside. Phones: stacked, panel capped at 360px. */
          @media (min-width: 1024px) {
            .hj-replay-grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 20px; align-items: stretch; margin-bottom: 22px; }
            .hj-replay-grid > .hj-replay-player { margin-bottom: 0 !important; }
            .hj-replay-cell { position: relative; min-height: 0; }
            .hj-replay-chapters { position: absolute; inset: 0; max-height: none !important; margin-bottom: 0 !important; }
          }
        `}</style>
      )}
      {/* Content Viewer */}
      {activeVideo && !isActiveLocked && (() => {
        const ft = activeVideo.fileType || 'video';
        if (ft === 'video') {
          const muxReady =
            activeVideo.muxIngestStatus === 'ready' &&
            !!activeVideo.muxPlaybackId;
          const tok = muxTokens[activeVideoIndex];
          const useMux = muxReady && tok && tok.tokens.playback;
          return (
            <div
              className={`hj-replay-player ${isPortrait ? 'overflow-hidden flex justify-center' : 'aspect-video overflow-hidden'}`}
              style={{ background: '#000', borderRadius: 'var(--hj-r-md)', marginBottom: 22 }}
            >
              {/* Portrait episodes are letterboxed 9:16 center-stage (full-bleed on
                  mobile via width:100%); landscape fills the 16:9 box as before. */}
              <div style={isPortrait ? { width: 'min(100%, 44vh)', aspectRatio: '9 / 16', overflow: 'hidden' } : { width: '100%', height: '100%' }}>
                {useMux ? (
                  <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'oklch(0.7 0 0)' }} /></div>}>
                    <MuxPlayer
                      key={`mux-${activeVideo.muxPlaybackId}`}
                      ref={playerRef}
                      playbackId={activeVideo.muxPlaybackId!}
                      tokens={{
                        playback: tok!.tokens.playback!,
                        thumbnail: tok!.tokens.thumbnail || undefined,
                        storyboard: tok!.tokens.storyboard || undefined,
                      }}
                      autoPlay
                      // A replay can open past the small talk at the top (product.videos[i].startSec,
                      // Joe Oct 27 2026: Joe Burke's first 6 minutes). Scrubbing back still works.
                      startTime={Number((activeVideo as any).startSec) > 0 ? Number((activeVideo as any).startSec) : undefined}
                      defaultHiddenCaptions
                      style={{ width: '100%', height: '100%' }}
                      metadata={{ video_title: activeVideo.title || 'Lesson' }}
                      onEnded={() => goToNextUnlocked(activeVideoIndex)}
                      onError={() => refetchMuxToken(activeVideoIndex)}
                      aria-label={activeVideo.title || 'Video player'}
                    />
                  </Suspense>
                ) : (
                  <video
                    key={activeVideo.url || activeVideo.s3Key}
                    ref={playerRef}
                    src={activeVideo.url}
                    className="w-full h-full"
                    controls
                    autoPlay
                    playsInline
                    onEnded={() => goToNextUnlocked(activeVideoIndex)}
                    aria-label={activeVideo.title || 'Video player'}
                  />
                )}
              </div>
            </div>
          );
        }
        if (ft === 'pdf') {
          return (
            <div className="flex flex-col gap-3" style={{ marginBottom: 22 }}>
              <div
                className="overflow-hidden"
                style={{
                  height: '70vh',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1px solid var(--hj-hairline)',
                  background: 'var(--hj-surface-1)',
                }}
              >
                <iframe src={activeVideo.url} className="w-full h-full" title={activeVideo.title || 'PDF'} />
              </div>
              <a
                href={activeVideo.url}
                download
                className="inline-flex items-center gap-1.5 self-start"
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 12.5,
                  fontWeight: 500,
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                  minHeight: 36,
                }}
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
            </div>
          );
        }
        if (ft === 'image') {
          return (
            <div style={{ marginBottom: 22 }}>
              <img
                src={activeVideo.url}
                alt={activeVideo.title || 'Image'}
                className="w-full object-contain"
                style={{ borderRadius: 'var(--hj-r-md)', maxHeight: '70vh' }}
              />
            </div>
          );
        }
        return (
          <div
            className="flex flex-col items-center justify-center gap-4"
            style={{
              padding: '64px 24px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 22,
            }}
          >
            <File className="w-10 h-10" style={{ color: 'var(--hj-ink-2)' }} />
            <p className="m-0" style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>
              {activeVideo.title || 'File'}
            </p>
            <a
              href={activeVideo.url}
              download
              className="inline-flex items-center gap-1.5"
              style={{
                padding: '9px 18px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                fontFamily: 'inherit',
                minHeight: 40,
              }}
            >
              <Download className="w-3.5 h-3.5" /> Download file
            </a>
          </div>
        );
      })()}

      {/* Replay chapters — generated from the recording, click to jump. Own scroll
          box (sticky beside the player on desktop) so browsing chapters never
          scrolls the page away from the video. */}
      {replayChapters && activeVideo && !isActiveLocked && (
        <div className="hj-replay-cell">
        <div
          className="hj-replay-chapters flex flex-col"
          style={{ marginBottom: 22, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', maxHeight: 360, overflow: 'hidden' }}
        >
          <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--hj-hairline)' }}>
            <span className="mono-label" style={{ color: 'var(--hj-ink)' }}>Chapters</span>
            <span style={{ fontSize: 12, color: 'var(--hj-ink-2)', fontVariantNumeric: 'tabular-nums' }}>
              {activeChapterIndex >= 0 ? `${activeChapterIndex + 1} of ${replayChapters.chapters.length}` : `${replayChapters.chapters.length} · ${formatClock(replayChapters.chapters[replayChapters.chapters.length - 1].endSec)}`}
            </span>
          </div>
          <ol ref={chapterListRef} className="m-0 flex flex-col gap-0.5" style={{ listStyle: 'none', padding: 8, overflowY: 'auto', minHeight: 0, position: 'relative' }}>
            {replayChapters.chapters.map((c, i) => {
              const active = i === activeChapterIndex;
              return (
                <li key={`${c.startSec}-${i}`}>
                  <button
                    type="button"
                    onClick={() => seekTo(c.startSec)}
                    className="w-full text-left flex items-start gap-3 transition-colors"
                    style={{ padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', background: active ? 'var(--hj-blue-tint)' : 'transparent', border: 0, cursor: 'pointer', boxShadow: active ? 'inset 3px 0 0 var(--hj-blue)' : 'none' }}
                    aria-current={active ? 'true' : undefined}
                  >
                    {/* Chapter number, then the timestamp under it, so "8 of 20" in
                        the panel header points at something in the list. */}
                    <span className="flex flex-col items-end flex-shrink-0" style={{ minWidth: 44, paddingTop: 1 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>{i + 1}</span>
                      <span className="mono-label" style={{ fontVariantNumeric: 'tabular-nums', color: active ? 'var(--hj-ink)' : 'var(--hj-ink-2)', marginTop: 2 }}>{formatClock(c.startSec)}</span>
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13.5, fontWeight: active ? 700 : 600, color: 'var(--hj-ink)', lineHeight: 1.3 }}>{c.title}</span>
                      {/* Only the playing chapter shows its summary; titles alone let
                          a dozen chapters fit beside the video. */}
                      {active && c.summary && <span style={{ display: 'block', fontSize: 12, color: 'var(--hj-ink)', opacity: 0.82, lineHeight: 1.4, marginTop: 3 }}>{c.summary}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        </div>
      )}
      </div>

      {/* Questions for the creator, right under the player as a thread (Joe, Oct 27
          2026: it was buried at the foot of the page). Once there is something to watch. */}
      {productId && (product.videos || []).some((v: any) => !v.locked && (v.muxPlaybackId || v.url)) && (
        <ReplayQuestions productId={productId} underPlayer />
      )}

      {/* About — the description, below the player where it doesn't push the video
          off screen. Collapsed when long. */}
      {product.description && (() => {
        const long = product.description.length > 420;
        const open = !long || aboutOpen;
        const label = product.productType === 'webinar' ? 'About this event' : 'About this course';
        return (
          <div style={{ marginBottom: 22, padding: '18px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
            <button
              type="button"
              onClick={() => long && setAboutOpen(o => !o)}
              className="flex items-center gap-1.5"
              style={{ background: 'none', border: 0, padding: 0, cursor: long ? 'pointer' : 'default', color: 'var(--hj-beige-ink)' }}
              aria-expanded={open}
            >
              <span className="mono-label" style={{ color: 'var(--hj-beige-ink-2)' }}>{label}</span>
              {long && (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
            </button>
            {replayChapters?.overview && (
              <p className="m-0" style={{ fontSize: 15, fontWeight: 600, color: 'var(--hj-beige-ink)', lineHeight: 1.55, marginTop: 10 }}>{replayChapters.overview}</p>
            )}
            <div
              className="flex flex-col gap-2.5 hj-about-body"
              style={{ marginTop: 12, maxHeight: open ? 'none' : 96, overflow: 'hidden', position: 'relative' }}
            >
              {renderDescription(product.description)}
              {!open && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 44, background: 'linear-gradient(transparent, var(--hj-beige))' }} />
              )}
            </div>
            {long && (
              <button type="button" onClick={() => setAboutOpen(o => !o)} style={{ marginTop: 8, background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)', textDecoration: 'underline' }}>
                {open ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        );
      })()}

      {/* Downloads — chapter files attached to the current lesson */}
      {activeVideo && !isActiveLocked && activeAttachments.length > 0 && (
        <div
          style={{
            marginBottom: 22,
            padding: 16,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="flex items-center gap-1.5" style={{ marginBottom: 10 }}>
            <Paperclip className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-2)' }} />
            <span className="mono-label" style={{ color: 'var(--hj-ink-2)' }}>Downloads</span>
          </div>
          <div className="flex flex-col gap-2">
            {/* Whole row is the download CTA — "DOWNLOAD <NAME> PDF" so it's
                unmistakably a worksheet to grab, not just a label (MYST feedback). */}
            {activeAttachments.map((a, i) => {
              const name = a.title || a.s3Key?.split('/').pop() || 'File';
              const label = a.fileType === 'image' ? `View ${name}` : `Download ${name} PDF`;
              const inner = (
                <>
                  {a.fileType === 'image'
                    ? <ImageIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-yellow-ink)' }} />
                    : <FileText className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(0 70% 60%)' }} />}
                  <span className="flex-1 text-sm truncate" style={{ color: 'var(--hj-ink)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 12.5 }}>
                    {label}
                  </span>
                  {a.url && <Download className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-yellow-ink)' }} />}
                </>
              );
              const rowStyle: React.CSSProperties = {
                padding: '12px 14px',
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline)',
                textDecoration: 'none',
                cursor: a.url ? 'pointer' : 'default',
              };
              return a.url ? (
                <a
                  key={a.id || a.s3Key || i}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3"
                  style={rowStyle}
                >
                  {inner}
                </a>
              ) : (
                <div key={a.id || a.s3Key || i} className="flex items-center gap-3" style={rowStyle}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked content placeholder */}
      {activeVideo && isActiveLocked && (
        <div
          className="aspect-video flex flex-col items-center justify-center gap-3"
          style={{
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px dashed var(--hj-hairline)',
            marginBottom: 22,
          }}
        >
          <Lock className="w-9 h-9" style={{ color: 'var(--hj-ink-2)' }} />
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink-2)', fontWeight: 500 }}>
            This content is not yet available
          </p>
          {activeVideo.availableDate && (
            <p
              className="m-0 tabular"
              style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}
            >
              Unlocks {new Date(activeVideo.availableDate).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Mark as Complete Button */}
      {activeVideo && !isActiveLocked && (
        <div style={{ marginBottom: 22 }}>
          {(() => {
            const done = !!progressMap[activeVideo.s3Key];
            return (
              <button
                type="button"
                disabled={togglingProgress}
                onClick={() => handleToggleComplete(activeVideo.s3Key)}
                className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  background: done ? 'transparent' : 'var(--hj-blue)',
                  border: `1px solid ${done ? 'var(--hj-blue)' : 'var(--hj-blue)'}`,
                  color: done ? 'var(--hj-blue)' : 'oklch(0.18 0.005 70)',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: togglingProgress ? 'not-allowed' : 'pointer',
                  opacity: togglingProgress ? 0.6 : 1,
                  minHeight: 36,
                }}
              >
                {togglingProgress ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : done ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <Circle className="w-3.5 h-3.5" />
                )}
                {done ? 'Mark as incomplete' : 'Mark as complete'}
              </button>
            );
          })()}
        </div>
      )}

      {/* Content List (for series/bundle) */}
      {product.videos.length > 1 && (() => {
        const hasModules = product.modules && product.modules.length > 0;

        const renderVideoRow = (video: typeof product.videos[0], flatIndex: number, displayNum: number) => {
          const isLocked = video.locked;
          const ft = video.fileType || 'video';
          const fileIconColor = isLocked
            ? 'var(--hj-ink-2)'
            : ft === 'pdf'
              ? 'hsl(0 70% 60%)'
              : ft === 'image'
                ? 'var(--hj-blue)'
                : 'var(--hj-ink-2)';
          const FileIcon = isLocked
            ? Lock
            : ft === 'pdf'
              ? FileText
              : ft === 'image'
                ? ImageIcon
                : ft === 'file'
                  ? File
                  : Video;
          const active = flatIndex === activeVideoIndex && !isLocked;
          return (
            <button
              key={video.s3Key || flatIndex}
              type="button"
              onClick={() => !isLocked && setActiveVideoIndex(flatIndex)}
              disabled={isLocked}
              className="w-full flex items-center gap-3 text-left transition-colors disabled:cursor-not-allowed"
              style={{
                padding: 10,
                borderRadius: 'var(--hj-r-sm)',
                background: active
                  ? 'var(--hj-blue-tint)'
                  : 'transparent',
                color: active ? 'var(--hj-blue)' : 'var(--hj-ink)',
                opacity: isLocked ? 0.5 : 1,
                border: 0,
                fontFamily: 'inherit',
                cursor: isLocked ? 'not-allowed' : 'pointer',
              }}
            >
              <span
                className="tabular flex-shrink-0"
                style={{
                  width: 22,
                  textAlign: 'right',
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: 'var(--hj-ink-2)',
                  fontFamily: 'var(--hj-font-mono)',
                }}
              >
                {displayNum}
              </span>
              <FileIcon className="w-4 h-4 flex-shrink-0" style={{ color: fileIconColor }} />
              <span className="flex-1 text-sm truncate" style={{ color: 'inherit' }}>
                {video.title || `Item ${displayNum}`}
              </span>
              {isLocked && video.availableDate && (
                <span
                  className="tabular flex-shrink-0"
                  style={{ fontSize: 11, color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}
                >
                  {new Date(video.availableDate).toLocaleDateString()}
                </span>
              )}
              {!isLocked && progressMap[video.s3Key] && (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} />
              )}
              {!isLocked && !progressMap[video.s3Key] && (
                <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-4)', opacity: 0.5 }} />
              )}
            </button>
          );
        };

        if (hasModules) {
          let flatIdx = 0;
          return (
            <div className="flex flex-col gap-3">
              {product.modules!.map(mod => {
                const moduleStartIdx = flatIdx;
                const isExpanded = expandedModules[mod.id] !== false;
                const moduleLessons = mod.lessons.map((lesson, li) => ({
                  lesson,
                  flatIndex: moduleStartIdx + li,
                  // Continuous chapter numbers across the whole course (1–12),
                  // not restarting per module (David's MYST feedback).
                  displayNum: moduleStartIdx + li + 1,
                }));
                flatIdx += mod.lessons.length;

                const completedInModule = moduleLessons.filter(ml => {
                  const v = product.videos[ml.flatIndex];
                  return v && !v.locked && progressMap[v.s3Key];
                }).length;
                const totalInModule = moduleLessons.filter(ml => {
                  const v = product.videos[ml.flatIndex];
                  return v && !v.locked;
                }).length;

                return (
                  <div
                    key={mod.id}
                    style={{
                      borderRadius: 'var(--hj-r-md)',
                      background: 'var(--hj-surface-1)',
                      border: '1px solid var(--hj-hairline)',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedModules(prev => ({ ...prev, [mod.id]: !isExpanded }))}
                      className="w-full flex items-center gap-3 text-left transition-colors"
                      style={{
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--hj-ink-2)' }} />
                      ) : (
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--hj-ink-2)' }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className="truncate m-0"
                          style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}
                        >
                          {mod.title}
                        </p>
                        <div
                          className="mono-label"
                          style={{ marginTop: 2, color: 'var(--hj-ink-2)' }}
                        >
                          {mod.lessons.length} lesson{mod.lessons.length !== 1 ? 's' : ''}
                          {totalInModule > 0 && ` · ${completedInModule}/${totalInModule} complete`}
                        </div>
                      </div>
                    </button>
                    {isExpanded && (
                      <div style={{ padding: '0 8px 8px' }}>
                        <div className="flex flex-col" style={{ gap: 2 }}>
                          {moduleLessons.map(({ flatIndex, displayNum }) => {
                            const video = product.videos[flatIndex];
                            if (!video) return null;
                            return renderVideoRow(video, flatIndex, displayNum);
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div
            style={{
              padding: 16,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <div className="mono-label" style={{ marginBottom: 12, color: 'var(--hj-ink-2)' }}>
              {(() => {
                const counts: Record<string, number> = {};
                product.videos.forEach(v => {
                  const t = v.fileType || 'video';
                  counts[t] = (counts[t] || 0) + 1;
                });
                const parts: string[] = [];
                if (counts.video) parts.push(`${counts.video} video${counts.video > 1 ? 's' : ''}`);
                if (counts.pdf) parts.push(`${counts.pdf} PDF${counts.pdf > 1 ? 's' : ''}`);
                if (counts.image) parts.push(`${counts.image} image${counts.image > 1 ? 's' : ''}`);
                if (counts.file) parts.push(`${counts.file} file${counts.file > 1 ? 's' : ''}`);
                return parts.join(' · ') || `${product.videos.length} items`;
              })()}
            </div>
            <div className="flex flex-col" style={{ gap: 2 }}>
              {product.videos.map((video, index) => renderVideoRow(video, index, index + 1))}
            </div>
          </div>
        );
      })()}

      {/* Reviews Section */}
      <section style={{ marginTop: 32 }} className="flex flex-col gap-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2
            className="font-hj-display m-0"
            style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}
          >
            Reviews
          </h2>
          {totalReviews > 0 && (
            <span
              className="inline-flex items-center gap-2 tabular"
              style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}
            >
              <span style={{ color: 'var(--hj-yellow-ink)' }}>
                {'★'.repeat(Math.round(averageRating))}
                {'☆'.repeat(5 - Math.round(averageRating))}
              </span>
              {averageRating.toFixed(1)} ({totalReviews})
            </span>
          )}
        </div>

        <div
          className="flex flex-col gap-3"
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <h3
            className="m-0"
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink-2)' }}
          >
            Write a review
          </h3>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className="transition-colors"
                onMouseEnter={() => setReviewHover(star)}
                onMouseLeave={() => setReviewHover(0)}
                onClick={() => setReviewRating(star)}
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 2,
                  cursor: 'pointer',
                  fontSize: 22,
                  color: (reviewHover || reviewRating) >= star ? 'var(--hj-yellow)' : 'var(--hj-ink-4)',
                  fontFamily: 'inherit',
                }}
              >
                ★
              </button>
            ))}
            {reviewRating > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
                {reviewRating} star{reviewRating !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <Textarea
            placeholder="Share your experience (optional, max 500 characters)"
            value={reviewComment}
            onChange={e => setReviewComment(e.target.value.substring(0, 500))}
            rows={3}
          />
          <div className="flex items-center justify-between">
            <span className="mono-label tabular" style={{ color: 'var(--hj-ink-4)', fontFamily: 'var(--hj-font-mono)' }}>
              {reviewComment.length}/500
            </span>
            <button
              type="button"
              onClick={handleSubmitReview}
              disabled={reviewRating < 1 || submittingReview}
              className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: reviewRating < 1 || submittingReview ? 'not-allowed' : 'pointer',
                opacity: reviewRating < 1 || submittingReview ? 0.5 : 1,
                minHeight: 36,
              }}
            >
              {submittingReview && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Submit review
            </button>
          </div>
        </div>

        {reviews.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {reviews.map(review => (
              <div
                key={review._id}
                style={{
                  padding: 14,
                  borderRadius: 'var(--hj-r-md)',
                  background: 'var(--hj-surface-1)',
                  border: '1px solid var(--hj-hairline)',
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="tabular" style={{ color: 'var(--hj-yellow-ink)', fontSize: 13 }}>
                      {'★'.repeat(review.rating)}
                      {'☆'.repeat(5 - review.rating)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--hj-ink-2)' }}>
                      {review.buyerEmail}
                    </span>
                  </div>
                  <span
                    className="mono-label tabular"
                    style={{ color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}
                  >
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {review.comment && (
                  <p
                    className="m-0"
                    style={{ marginTop: 8, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}
                  >
                    {review.comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p
            className="m-0"
            style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}
          >
            No reviews yet. Be the first to review!
          </p>
        )}
      </section>
    </main>
  );
};

export default ProductViewerPage;
